import React, { useState } from 'react';
import { useLocalization } from '../contexts/LocalizationContext';
import api, { getApiImageUrl } from '../services/api';
import './AddPlayerModal.css';

const AddPlayerModal = ({ isOpen, onClose, onPlayerAdded, isFirstPlayer = false }) => {
  const { t } = useLocalization();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    birthYear: '',
    sex: '',
    schoolName: '',
    avatar: ''
  });

  const [errors, setErrors] = useState({});
  const [availableAvatars, setAvailableAvatars] = useState([]);

  // School name is now optional free text field

  // Load available avatars on component mount
  React.useEffect(() => {
    const loadAvatars = async () => {
      try {
        const response = await api.get('/api/avatars');
        setAvailableAvatars(response.data.avatars);
        if (response.data.avatars.length > 0 && !formData.avatar) {
          setFormData(prev => ({ ...prev, avatar: response.data.avatars[0] }));
        }
      } catch (error) {
        console.error('Failed to load avatars:', error);
        // Fallback to some default avatars
        setAvailableAvatars(['Panda.png', 'Fox.png', 'Rabbit.png', 'Penguin.png']);
      }
    };
    
    if (isOpen) {
      loadAvatars();
  // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [isOpen]);

  // Function to generate color from filename hash
  const getAvatarColor = (filename) => {
    let hash = 0;
    for (let i = 0; i < filename.length; i++) {
      const char = filename.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Generate HSL color with higher saturation and better contrast
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 85%, 75%)`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t('validation.required');
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = t('validation.required');
    }
    if (!formData.birthYear) {
      newErrors.birthYear = t('validation.required');
    } else if (formData.birthYear <= 1900) {
      newErrors.birthYear = `Birth year must be after 1900`;
    }
    if (!formData.sex) {
      newErrors.sex = t('validation.required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showMessage = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/api/players', {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        birth_year: parseInt(formData.birthYear),
        sex: formData.sex,
        school_name: formData.schoolName || null,
        avatar: formData.avatar || availableAvatars[0]
      });

      showMessage(t('addPlayer.successMessage'), 'success');
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        birthYear: '',
        sex: '',
        schoolName: '',
        avatar: availableAvatars[0] || ''
      });

      // Notify parent component
      if (onPlayerAdded) {
        onPlayerAdded(response.data.player);
      }

      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to add player';
      showMessage(errorMessage, 'error');
    }

    setLoading(false);
  };

  const handleClose = () => {
    if (!isFirstPlayer) {
      onClose();
    }
    // If it's the first player, don't allow closing without adding a player
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2>{t('addPlayer.title')}</h2>
          {!isFirstPlayer && (
            <button className="close-button" onClick={handleClose}>×</button>
          )}
        </div>

        <div className="modal-content">
          {isFirstPlayer && (
            <div className="welcome-message">
              <p>{t('addPlayer.welcomeMessage')}</p>
            </div>
          )}

          {!isFirstPlayer && (
            <p className="modal-subtitle">{t('addPlayer.subtitle')}</p>
          )}

          {message && (
            <div className={`message ${messageType}`}>
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="player-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">{t('addPlayer.firstName')}</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={errors.firstName ? 'error' : ''}
                />
                {errors.firstName && <span className="error-text">{errors.firstName}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="lastName">{t('addPlayer.lastName')}</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={errors.lastName ? 'error' : ''}
                />
                {errors.lastName && <span className="error-text">{errors.lastName}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="birthYear">{t('addPlayer.birthYear')}</label>
                <input
                  type="number"
                  id="birthYear"
                  name="birthYear"
                  min={new Date().getFullYear() - 15}
                  max={new Date().getFullYear() - 7}
                  value={formData.birthYear}
                  onChange={handleInputChange}
                  className={errors.birthYear ? 'error' : ''}
                />
                {errors.birthYear && <span className="error-text">{errors.birthYear}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="sex">{t('addPlayer.sex')}</label>
                <select
                  id="sex"
                  name="sex"
                  value={formData.sex}
                  onChange={handleInputChange}
                  className={errors.sex ? 'error' : ''}
                >
                  <option value="">--</option>
                  <option value="M">{t('addPlayer.male')}</option>
                  <option value="F">{t('addPlayer.female')}</option>
                </select>
                {errors.sex && <span className="error-text">{errors.sex}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="schoolName">
                {t('addPlayer.schoolName')} 
                <span className="optional-label"> ({t('common.optional') || 'optional'})</span>
              </label>
              <input
                type="text"
                id="schoolName"
                name="schoolName"
                value={formData.schoolName}
                onChange={handleInputChange}
                placeholder={t('addPlayer.schoolNamePlaceholder') || 'e.g. OŠ Matije Gupca'}
              />
            </div>

            <div className="form-group">
              <label>{t('addPlayer.avatar')}</label>
              <div className="avatar-selector">
                {availableAvatars.map(avatar => (
                  <button
                    key={avatar}
                    type="button"
                    className={`avatar-option ${formData.avatar === avatar ? 'selected' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, avatar }))}
                    style={{ backgroundColor: getAvatarColor(avatar) }}
                  >
                    <img 
                      src={getApiImageUrl(`/api/avatars/${avatar}`)} 
                      alt={avatar.replace('.png', '')}
                      className="avatar-image"
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="save-button"
                disabled={loading}
              >
                {loading ? '...' : t('addPlayer.saveButton')}
              </button>
              {!isFirstPlayer && (
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={handleClose}
                >
                  {t('addPlayer.cancelButton')}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddPlayerModal;